<template>
  <GcBaseLayout
    ref="baseLayout"
    :title="title"
    :isXSmall="isXSmall"
    :isLarge="isLarge"
    :baseView="baseView"
    :key="pageKey"
    :tabs="tabs"
    :selectedIndex="selectedIndex"
    :backButtonEnable="true"
    :backButtonFunction="backButtonFunction"
    :formsConfig="formsConfig"
  >
    <template #headerContent>
      <GcHeaderContentDynamic
        title="Customer"
        :baseView="baseView"
        :primaryLabel="model.Name"
        :secondaryLabel="baseView.id"
        recordType="Customer"
        v-if="loaded"
      />
    </template>

    <div class="css-v2 customer-dashboard" v-if="loaded">
      <div class="db-toolbar">
        <span class="db-toolbar-title">Dashboard</span>
        <div class="db-toolbar-actions">
          <span class="db-toolbar-period">{{ periodLabel }}</span>
          <DxButton
            text="Refresh"
            icon="refresh"
            :disabled="isRefreshing"
            :element-attr="{
              class: isRefreshing ? 'db-refresh-btn spinning' : 'db-refresh-btn',
            }"
            @click="refreshDashboard"
          />
        </div>
      </div>

      <div class="db-grid">
        <gc-dashboard-ytd-card
          v-show="!expandedCard"
          ref="ytd"
          class="span2"
          :customer-code="model.Code"
          :balances-summary="balancesSummary"
        />

        <gc-dashboard-sales-performance-card
          v-show="!expandedCard"
          ref="salesPerformance"
          :customer-code="model.Code"
        />

        <gc-dashboard-card
          v-show="!expandedCard"
          ref="customerBalancesCard"
          title="Customer Balances"
          :fetch="fetchCustomerBalances"
        >
          <template #header-extra>
            <div class="db-balance-toggle-group">
              <button
                v-for="tab in balancesViewTabs"
                :key="tab.id"
                type="button"
                class="db-balance-toggle-btn"
                :class="{ active: balancesTabIndex === tab.id }"
                @click="balancesTabIndex = tab.id"
              >
                {{ tab.text }}
              </button>
            </div>
          </template>
          <template #default="{ data }">
            <gc-dashboard-customer-balances-card
              :summary="data"
              :selected-index="balancesTabIndex"
              @bucket-click="onBalanceBucketClick"
            />
          </template>
        </gc-dashboard-card>

        <gc-dashboard-card
          v-show="!expandedCard || expandedCard === 'productHistory'"
          ref="productHistoryCard"
          class="span2"
          title="Product History"
          subtitle="Last invoiced by item — click row for full history"
          :fetch="fetchProductHistory"
          expandable
          :expanded="expandedCard === 'productHistory'"
          hide-header
          @toggle-expand="toggleExpand('productHistory')"
        >
          <template #default="{ data, expanded, bodyHeight }">
            <gc-dashboard-product-history-card
              title="Product History"
              subtitle="Last invoiced by item — click row for full history"
              :product-history="data"
              :grid-height="expanded ? bodyHeight : 360"
              expandable
              :expanded="expanded"
              @toggle-expand="toggleExpand('productHistory')"
              @row-click="onProductHistoryRowClick"
            />
          </template>
        </gc-dashboard-card>

        <gc-dashboard-card
          v-show="!expandedCard || expandedCard === 'transactions'"
          ref="transactionsCard"
          class="span2"
          title="Transactions"
          :fetch="fetchTransactions"
          expandable
          :expanded="expandedCard === 'transactions'"
          hide-header
          @toggle-expand="toggleExpand('transactions')"
        >
          <template #default="{ data, expanded, bodyHeight }">
            <gc-dashboard-transactions-card
              title="Transactions"
              :transactions="data"
              :active-filter.sync="transactionsFilter"
              :grid-height="expanded ? bodyHeight : 360"
              expandable
              :expanded="expanded"
              @toggle-expand="toggleExpand('transactions')"
              @row-click="onTransactionRowClick"
            />
          </template>
        </gc-dashboard-card>
      </div>

      <gc-popup-customer-balance-invoices
        :visible.sync="balanceInvoicesPopup.visible"
        :customer-code="model.Code"
        :term-type="balanceInvoicesPopup.termType"
        :term="balanceInvoicesPopup.term"
        :currency-code="balancesSummaryFxCurrencyCode"
        :bucket-label="balanceInvoicesPopup.bucketLabel"
      />

      <GcPopupCustomerProductHistoryDtl
        v-if="productHistoryDetailPopup.visible"
        :setVisible="productHistoryDetailPopup.visible"
        :hidden="closeProductHistoryDetailPopup"
        :setLoading="() => {}"
        :dataObj="productHistoryDetailPopup.dataModel"
        :filterByCustomer="true"
      />

      <gc-popup-customer-transaction-detail
        v-if="selectedTransaction"
        :visible.sync="transactionDetailPopupVisible"
        :transaction="selectedTransaction"
      />
    </div>
  </GcBaseLayout>
</template>

<script>
import RouteMixin from "@/_mixins/route.mixin";
import CustomerTabsMixin from "@/_mixins/customer-tabs.mixin";
import { DxButton } from "devextreme-vue/button";
import { CustomerService } from "../../_services/api/customers/customer.service";
import GcDashboardSalesPerformanceCard from "@/components/customer/gc-dashboard-sales-performance-card.vue";
import GcDashboardYtdCard from "@/components/customer/gc-dashboard-ytd-card.vue";
import { CustomerBalanceService } from "../../_services/api/customers/customer-balance.service";
import { CustomerProductHistoryService } from "../../_services/api/customers/customer-product-history.service";
import { CustomerTransactionService } from "../../_services/api/customers/customer-transaction.service";
import { getDefaultDateSettings } from "@/_store/helpers";
import GcDashboardCard from "@/components/customer/gc-dashboard-card";
import GcDashboardCustomerBalancesCard from "@/components/customer/gc-dashboard-customer-balances-card";
import GcDashboardProductHistoryCard from "@/components/customer/gc-dashboard-product-history-card";
import GcDashboardTransactionsCard from "@/components/customer/gc-dashboard-transactions-card";
import GcPopupCustomerBalanceInvoices from "@/popups/customers/gc-popup-customer-balance-invoices";
import GcPopupCustomerProductHistoryDtl from "@/popups/gc-popup-customer-product-history-detail";
import GcPopupCustomerTransactionDetail from "@/popups/customers/gc-popup-customer-transaction-detail";

export default {
  mixins: [RouteMixin, CustomerTabsMixin],
  components: {
    DxButton,
    GcDashboardCard,
    GcDashboardSalesPerformanceCard,
    GcDashboardYtdCard,
    GcDashboardCustomerBalancesCard,
    GcDashboardProductHistoryCard,
    GcDashboardTransactionsCard,
    GcPopupCustomerBalanceInvoices,
    GcPopupCustomerProductHistoryDtl,
    GcPopupCustomerTransactionDetail,
  },
  data() {
    return {
      backButtonRoute: `/customer`,
      model: {
        Code: "",
        Name: "",
      },
      loaded: false,
      isRefreshing: false,
      balancesSummary: null,
      balanceInvoicesPopup: {
        visible: false,
        termType: "",
        term: "",
        bucketLabel: "",
      },
      productHistoryDetailPopup: {
        visible: false,
        dataModel: {},
      },
      selectedTransaction: null,
      transactionDetailPopupVisible: false,
      transactionsFilter: "all",
      expandedCard: null,
      balancesTabIndex: 0,
      balancesViewTabs: [
        { id: 0, text: "Age By Terms" },
        { id: 1, text: "Age By Period" },
      ],
    };
  },
  computed: {
    balancesSummaryFxCurrencyCode() {
      return (this.balancesSummary && this.balancesSummary.FxCurrencyCode) || "";
    },
    periodLabel() {
      const fy = getDefaultDateSettings().currentFiscalYear;
      const thisYear = parseInt(fy, 10);
      if (isNaN(thisYear)) return "";
      return `FY ${thisYear - 1}/${String(thisYear).slice(-2)} · Full Year`;
    },
  },
  async created() {
    this.$store.dispatch("defaultDateSettings/setCurrentFiscalData");

    await CustomerService.getByCode(this.baseView.id, ["Code", "Name"]).then(
      (response) => {
        if (response.data.value.length > 0) {
          this.model.Code = response.data.value[0].Code;
          this.model.Name = response.data.value[0].Name;
        }
      },
    );

    this.selectedIndex = this.tabs.findIndex((x) => x.text === "Dashboard");
    this.loaded = true;
  },
  mounted() {
    window.addEventListener("keydown", this.onKeydown);
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.onKeydown);
  },
  methods: {
    onKeydown(e) {
      if (e.key === "Escape" && this.expandedCard) {
        this.expandedCard = null;
        this.scrollDashboardToTop();
      }
    },
    toggleExpand(key) {
      this.expandedCard = this.expandedCard === key ? null : key;
      this.scrollDashboardToTop();
    },
    scrollDashboardToTop() {
      this.$nextTick(() => {
        if (this.$refs.baseLayout) this.$refs.baseLayout.scrollView.scrollTo(0);
      });
    },
    backButtonFunction() {
      this.$router.push({
        path: this.backButtonRoute,
      });
    },
    fetchTransactions() {
      return CustomerTransactionService.getByCustomerCode(this.baseView.id).then(
        (data) => data || []
      );
    },
    onTransactionRowClick(rowData) {
      this.selectedTransaction = rowData;
      this.transactionDetailPopupVisible = true;
    },
    fetchCustomerBalances() {
      return CustomerBalanceService.getSummary(this.baseView.id).then(
        (response) => {
          this.balancesSummary = response.data;
          return response.data;
        }
      );
    },
    fetchProductHistory() {
      return CustomerProductHistoryService.getByCustomerCode(this.baseView.id).then(
        (response) => response.data.value || []
      );
    },
    onBalanceBucketClick({ termType, term, bucketLabel }) {
      this.balanceInvoicesPopup = { visible: true, termType, term, bucketLabel };
    },
    onProductHistoryRowClick(rowData) {
      this.productHistoryDetailPopup = { visible: true, dataModel: rowData };
    },
    closeProductHistoryDetailPopup() {
      this.productHistoryDetailPopup.visible = false;
    },
    async refreshDashboard() {
      this.isRefreshing = true;
      this.balancesSummary = null;

      const cardRefreshPromises = Object.values(this.$refs)
        .filter((ref) => ref && typeof ref.refresh === "function")
        .map((ref) => ref.refresh());

      if (cardRefreshPromises.length > 0) {
        await Promise.allSettled(cardRefreshPromises);
      } else {
        await new Promise((resolve) => setTimeout(resolve, 600));
      }

      this.isRefreshing = false;
      this.$notify("Dashboard refreshed", "success");
    },
  },
};
</script>

<style lang="scss">
.customer-dashboard {
  padding: 12px 16px;

  .db-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #fff;
    border: 1px solid var(--base-border-color, #e0e0e0);
    border-radius: 6px;
    padding: 10px 14px;
    margin-bottom: 10px;

    &-title {
      font-size: 13px;
      font-weight: 600;
    }

    &-actions {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    &-period {
      font-size: 11px;
      color: #6b7280;
      border: 1px solid var(--base-border-color, #e0e0e0);
      border-radius: 6px;
      padding: 6px 10px;
    }

    .db-refresh-btn.spinning .dx-icon {
      animation: customer-dashboard-spin 0.65s linear infinite;
    }
  }

  .db-grid {
    display: grid;
    grid-template-columns: 1.55fr 1fr;
    gap: 10px;

    .span2 {
      grid-column: 1 / -1;
    }
  }

  .db-balance-toggle-group {
    grid-column: 3;
    justify-self: end;
    display: inline-flex;
    background: rgba(53, 186, 246, 0.08);
    border-radius: 9999px;
    padding: 3px;
  }

  .db-balance-toggle-btn {
    background: transparent;
    border: 0;
    border-radius: 9999px;
    padding: 6px 14px;
    font-size: 11px;
    font-weight: 500;
    color: #475467;
    cursor: pointer;
    font-family: Inter, Roboto, sans-serif;

    &.active {
      background: #35baf6;
      color: #fff;
    }

    &:hover:not(.active) {
      background: rgba(53, 186, 246, 0.14);
    }
  }

}

@keyframes customer-dashboard-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
